

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import APIEndPoints from '../../../../middleware/APIEndPoints';

// Replace with your Cloudinary upload preset and cloud name
const cloudName = process.env.REACT_APP_CLOUD_NAME;
const UPLOAD_PRESET = 'upload';
const VIDEO_PRESET = 'videos_presets';

const BlogManager = () => {
  const [categories, setCategories] = useState([]);
  const [blogs, setBlogs] = useState([]);

  // Category state
  const [categoryName, setCategoryName] = useState('');

  // Blog state
  const [blogTitle, setBlogTitle] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [blogCategory, setBlogCategory] = useState('');
  const [blogMedia, setBlogMedia] = useState(null);
  const [isVideo, setIsVideo] = useState(false);

  // State for filtering blogs
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');


  const getCategoryURL = APIEndPoints.get_categories.url;
  const getBlogsURL = APIEndPoints.get_blogs.url;

  // Fetch categories and blogs when the component mounts
  useEffect(() => {
    fetchCategories();
    fetchBlogs();
  }, []);

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      const res = await axios.get(getCategoryURL);
      setCategories(res.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Fetch Blogs
  const fetchBlogs = async () => {
    try {
      const res = await axios.get(getBlogsURL);
      setBlogs(res.data);
      setFilteredBlogs(res.data);  // Set all blogs as filtered by default
    } catch (err) {
      console.error('Error fetching blogs:', err);
    }
  };

  // Handle Category CRUD Operations
  const createCategory = async () => {
    try {
      const res = await axios.post(getCategoryURL, { name: categoryName });
      setCategories([...categories, res.data]);
      setCategoryName('');
    } catch (err) {
      console.error('Error creating category:', err);
    }
  };

  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${getCategoryURL}/${id}`);
      setCategories(categories.filter(category => category._id !== id));
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };


// Cloudinary Media Upload
const uploadMediaToCloudinary = async (file) => {
  const resourceType = file.type.startsWith('video') ? 'video' : 'image';
  
  // Construct the appropriate Cloudinary API URL
  const api = `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`;

  const formData = new FormData();
  formData.append('file', file);

  // Use the correct preset for videos and images
  if (resourceType === 'video') {
    formData.append('upload_preset', VIDEO_PRESET);
  } else {
    formData.append('upload_preset', UPLOAD_PRESET);
  }

  try {
    const res = await axios.post(api, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    // Log the result and return the URL
    if (res.data.resource_type === 'video') {
      console.log('Video uploaded:', res.data.secure_url);
    } else if (res.data.resource_type === 'image') {
      console.log('Image uploaded:', res.data.secure_url);
    }

    return res.data.secure_url;
  } catch (err) {
    console.error('Error uploading to Cloudinary:', err);
    return null;
  }
};


  // Handle Blog CRUD Operations
  const createBlog = async () => {
    try {
      // Upload media to Cloudinary if exists
      let mediaUrl = null;
      if (blogMedia) {
        mediaUrl = await uploadMediaToCloudinary(blogMedia);
      }

      const newBlog = {
        title: blogTitle,
        content: blogContent,
        categoryId: blogCategory,
        media: mediaUrl,
      };

      const res = await axios.post(getBlogsURL, newBlog);
      setBlogs([...blogs, res.data]);
      resetBlogForm();
      setFilteredBlogs([...blogs, res.data]);  // Update filtered blogs
    } catch (err) {
      console.error('Error creating blog:', err);
    }
  };

  const deleteBlog = async (id) => {
    try {
      await axios.delete(`${getBlogsURL}/${id}`);
      const updatedBlogs = blogs.filter(blog => blog._id !== id);
      setBlogs(updatedBlogs);
      setFilteredBlogs(updatedBlogs);  // Update filtered blogs
    } catch (err) {
      console.error('Error deleting blog:', err);
    }
  };

  // Reset blog form
  const resetBlogForm = () => {
    setBlogTitle('');
    setBlogContent('');
    setBlogCategory('');
    setBlogMedia(null);
    setIsVideo(false);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBlogMedia(file);
      setIsVideo(file.type.startsWith('video'));
    }
  };

  // Handle filtering blogs by category
  const handleCategoryFilter = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId === '') {
      setFilteredBlogs(blogs);  // Show all blogs if no category is selected
    } else {
      const filtered = blogs.filter(blog => blog.categoryId === categoryId);
      setFilteredBlogs(filtered);
    }
  };

  return (
    <div className="bg-[#1d0c39]  min-h-screen p-8 text-white">
      <h1>Blog Manager</h1>

      {/* CATEGORY SECTION */}
      <section className='bg-black'>
        <h2>Create Category</h2>
        <input
          type="text"
          className='text-black'
          value={categoryName}
          onChange={(e) => setCategoryName(e.target.value)}
          placeholder="Category Name"
        />
        <button onClick={createCategory}>Add Category</button>

        <h3>All Categories</h3>
        <ul>
          {categories.map(category => (
            <li key={category._id}>
              {category.name}
              <button onClick={() => deleteCategory(category._id)}>Delete</button>
            </li>
          ))}
        </ul>
      </section>

      {/* BLOG SECTION */}
      <section>
        <h2>Create Blog</h2>
        <input
          type="text"
          className='text-black'
          value={blogTitle}
          onChange={(e) => setBlogTitle(e.target.value)}
          placeholder="Blog Title"
        />
        <textarea
          value={blogContent}
          className='text-black'
          onChange={(e) => setBlogContent(e.target.value)}
          placeholder="Blog Content"
        />
        <select value={blogCategory} onChange={(e) => setBlogCategory(e.target.value)} className='text-black'>
          <option value="">Select Category</option>
          {categories.map(category => (
            <option key={category._id} value={category._id}>
              {category.name}
            </option>
          ))}
        </select>

        {/* File Input for Media */}
        <input type="file" onChange={handleFileChange} />
        {blogMedia && (
          <div>
            <p>Preview:</p>
            {isVideo ? (
              <video width="200" controls>
                <source src={URL.createObjectURL(blogMedia)} type={blogMedia.type} />
              </video>
            ) : (
              <img src={URL.createObjectURL(blogMedia)} alt="Preview" width="200" />
            )}
          </div>
        )}

        <button onClick={createBlog}>Add Blog</button>

        <h3>All Blogs</h3>
        <ul>
          {filteredBlogs.map(blog => (
            <li key={blog._id}>
              <h4>{blog.title}</h4>
              <p>{blog.content}</p>
              {blog.media && (
                blog.media.endsWith('.mp4') ? (
                  <video width="200" controls>
                    <source src={blog.media} type="video/mp4" />
                  </video>
                ) : (
                  <img src={blog.media} alt={blog.title} width="200" />
                )
              )}
              <button onClick={() => deleteBlog(blog._id)}>Delete</button>
            </li>
          ))}
        </ul>
      </section>

      {/* FILTER SECTION */}
      <section>
        <h2>Filter Blogs by Category</h2>
        <select value={selectedCategory} onChange={(e) => handleCategoryFilter(e.target.value)} className='text-black'>
          <option value="">All Categories</option>
          {categories.map(category => (
            <option key={category._id} value={category._id} className='text-black'>
              {category.name}
            </option>
          ))}
        </select>

        <h3>Filtered Blogs</h3>
        <ul>
          {filteredBlogs.map(blog => (
            <li key={blog._id}>
              <h4>{blog.title}</h4>
              <p>{blog.content}</p>
              {blog.media && (
                blog.media.endsWith('.mp4') ? (
                  <video width="200" controls>
                    <source src={blog.media} type="video/mp4" />
                  </video>
                ) : (
                  <img src={blog.media} alt={blog.title} width="200" />
                )
              )}
              <button onClick={() => deleteBlog(blog._id)}>Delete</button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default BlogManager;
